import * as Tone from 'tone';
import { Seconds, TransportTime } from 'tone/build/esm/core/type/Units';
import { Instrument, InstrumentOptions } from 'tone/build/esm/instrument/Instrument';
import { NoteSequence } from '@magenta/music/es6/protobuf';
import { BaseCurve } from './curves';
import EventEmitter from './EventEmitter';
import MalakaiTransport from './MalakaiTransport';
import { MalakaiInstrument, Progress } from './types';
import type Malakai from './Malakai';

export enum MalakaiLayerEvent {
  /** Generated note is scheduled for playback */
  Note = 'note',
  /** Song has reached a peak along the curve */
  Peak = 'peak',
  /** Song has reached a valley along the curve */
  Valley = 'valley',
  /** Song has reached an inflection point along the curve */
  Inflection = 'inflection',
}

export interface MalakaiLayerContext {
  /** Used for controlling external layer activations */
  malakai: Malakai;
  /** Used for layer state and instruments */
  layer: MalakaiLayer;
  /** Used for scheduling events */
  transport: MalakaiTransport;
}

export interface MalakaiLayerEventPayload {
  /** Time along the Malakai transport to schedule events */
  time: Seconds;
  /** Progress of the song at MalakaiEventPayload.time */
  progress: Progress;
  /** [MalakaiLayerEvent.Note] Note frequency */
  frequency?: Tone.Unit.Frequency;
  /** [MalakaiLayerEvent.Note] Note duration */
  duration?: Seconds;
  /** Original generated note */
  originalNote?: NoteSequence.INote;
}

export interface MalakaiLayerEventCallback {
  (context: MalakaiLayerContext, time: Seconds): void;
}

export interface MalakaiLayerHookCallback {
  (context: MalakaiLayerContext, event: MalakaiLayerEventPayload): void;
}

export interface MalakaiConfigEvent {
  /** Start time of event */
  start: TransportTime;
  /** Callback to trigger at MalakaiConfigEvent.start */
  callback: MalakaiLayerEventCallback;
}

export interface MalakaiLayerLoop {
  /** Start time of loop */
  start: TransportTime;
  /** Loop interval from MalakaiConfigLoop.start */
  interval: TransportTime;
  /** Number of loops, defaults to infinity */
  iterations?: number;
  /** Callback to trigger with each loop iteration */
  callback: MalakaiLayerEventCallback;
}

export interface IMalakaiLayer {
  /** Unique name for layer */
  name: string;
  /** Is layer playback disabled */
  disabled?: boolean;
  /** Name of generator to consume */
  generator?: string;
  /** Tone.Instrument (Sampler, Synth, etc.) selection available to this layer */
  instruments?: {[name: string]: MalakaiInstrument};
  /** Looping song events scheduled on this layer */
  loops?: MalakaiLayerLoop[];
  /** Song events to schedule on this layer */
  events?: MalakaiConfigEvent[];
  /** Playback event listeners */
  hooks?: {[key in MalakaiLayerEvent]?: MalakaiLayerHookCallback};
}

export default class MalakaiLayer
  extends EventEmitter<MalakaiLayer, MalakaiLayerEvent, MalakaiLayerEventPayload> {
  active = true;

  curve: BaseCurve;

  instruments: {[name: string]: Instrument<InstrumentOptions>} = {};

  private malakai: Malakai;

  private transport: MalakaiTransport;

  private layer: IMalakaiLayer;

  private _initialized = false;

  get name(): string {
    return this.layer.name;
  }

  get generator(): string|undefined {
    return this.layer.generator;
  }

  get initialized() {
    return this._initialized;
  }

  get context(): MalakaiLayerContext {
    return {
      malakai: this.malakai,
      transport: this.transport,
      layer: this,
    };
  }

  constructor(
    malakai: Malakai,
    transport: MalakaiTransport,
    layer: IMalakaiLayer,
    curve: BaseCurve,
  ) {
    super();
    this.emitterContext = this;
    this.malakai = malakai;
    this.transport = transport;
    this.layer = layer;
    this.curve = curve;
    this.active = !layer.disabled;
  }

  async initialize(): Promise<MalakaiLayer> {
    // Initialize instruments
    if (this.layer.instruments) {
      const instrumentEntries = Object.entries(this.layer.instruments);
      const instruments = await Promise.all(instrumentEntries.map(([, i]) => i.instrument()));
      instruments.forEach((instrument, i) => {
        const instrumentName = instrumentEntries[i][0];
        this.instruments[instrumentName] = instrument.sync().toDestination();
      });
    }
    this._initialized = true;
    return this;
  }

  async scheduleEvents() {
    // Schedule Peak/Valley/Inflection events
    for (let i = 1; i < this.curve.controls.length; i += 1) {
      const [x, y] = this.curve.controls[i];
      // Schedule Peak/Valley events
      if (i < this.curve.controls.length - 1) {
        const time = this.transport.getTimeForProgress(x);
        const previousY = this.curve.controls[i - 1][1];
        const nextY = this.curve.controls[i + 1][1];
        let event: MalakaiLayerEvent|null = null;
        if (y >= previousY && y >= nextY) {
          event = MalakaiLayerEvent.Peak;
        } else if (y <= previousY && y <= nextY) {
          event = MalakaiLayerEvent.Valley;
        }
        if (event) {
          this.transport.scheduleOnce((t) => {
            if (!event) return;
            this.emit(event, { time: t, progress: x });
          }, time);
        }
      }
      // Schedule Inflection Events
      const previousX = this.curve.controls[i - 1][0];
      const inflectionProgress = previousX + (x - previousX) / 2;
      const inflectionTime = this.transport.getTimeForProgress(inflectionProgress);
      this.transport.scheduleOnce((t) => {
        this.emit(MalakaiLayerEvent.Inflection, {
          time: t,
          progress: inflectionProgress,
        });
      }, inflectionTime);
    }
    // Initialize layer.hooks
    if (this.layer.hooks) {
      Object.entries(this.layer.hooks).forEach(([type, callback]) => {
        if (callback) {
          this.on(type as MalakaiLayerEvent, (event) => {
            if (this.active) {
              callback(this.context, event);
            }
          });
        }
      });
    }
    // Initialize layer.loops
    if (this.layer.loops) {
      this.layer.loops.forEach(({
        start, interval, iterations, callback,
      }) => {
        this.transport.scheduleRepeat((time) => {
          if (this.active) {
            callback(this.context, time);
          }
        }, start, interval, iterations);
      });
    }
    // Initialize layer.events
    if (this.layer.events) {
      this.layer.events.forEach(({ start, callback }) => {
        this.transport.scheduleOnce((time) => {
          if (this.active) {
            callback(this.context, time);
          }
        }, Tone.Time(start).toSeconds());
      });
    }
  }

  triggerNote(note: NoteSequence.INote) {
    if (
      typeof note.startTime === 'number'
      && typeof note.endTime === 'number'
      && typeof note.pitch === 'number'
    ) {
      const frequency = Tone.Frequency(note.pitch, 'midi').toFrequency();
      const duration = note.endTime - note.startTime;
      this.transport.scheduleOnce((t) => {
        if (this.active) {
          this.emit(MalakaiLayerEvent.Note, {
            time: t,
            progress: this.transport.getProgressForTime(t),
            duration,
            frequency,
            originalNote: note,
          });
        }
      }, note.startTime);
    }
  }

  triggerNotes(notes: NoteSequence.INote[]) {
    notes.forEach((note) => {
      this.triggerNote(note);
    });
  }

  /** Just a quick and dirty approach to selecting instruments by their electronic value */
  selectElectronicInstrument(
    instruments: string[],
    electronic: number,
  ): Instrument<InstrumentOptions>|null {
    if (!this.layer.instruments) throw new Error(`Layer ${this.layer.name} has no instruments.`);
    const instrumentNameSet = new Set(instruments);
    const sortedInstruments = Object.entries(this.layer.instruments)
      .filter(([name]) => instrumentNameSet.has(name))
      .sort(
        ([, a], [, b]) => Math.abs(a.electronic - electronic) - Math.abs(b.electronic - electronic),
      );
    if (sortedInstruments.length) {
      const selectedMalakaiInstrument = sortedInstruments[0][0];
      return this.instruments[selectedMalakaiInstrument];
    }
    return null;
  }
}
